/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { useLayoutContext } from "../../../../ui/components/Layout/LayoutContext";
import { BEM } from "@adapptio/lib-components";
import { ToolbarButton } from "../../../../ui/components/Toolbar/ToolbarButton";
import { useDeepCompareCallback } from "use-deep-compare";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { EndpointModel } from "../../Common/EndpointModel";
import { DesignContext, DOC_ERROR_SEVERITY } from "@adapptio/lib-blueprint";
import { isFunction, offEvent, onEvent } from "@adapptio/lib-shared";

function sumErrorCounts(counts: { error: number; warning: number; info: number; hint: number }): number {
	return counts.error + counts.warning + counts.info + counts.hint;
}

export function getTotalErrorCounts(dCtx: DesignContext) {
	const dErrors = dCtx.getErrorTypeCounts();

	return {
		error: dErrors.error,
		warning: dErrors.warning,
		info: dErrors.info,
		hint: dErrors.hint
	};
}

export interface IEndpointEditorConsoleFiltersProps {
	model: EndpointModel;
	severityFilter?: Set<DOC_ERROR_SEVERITY> | null;
	onFilterSelect?: (filter: DOC_ERROR_SEVERITY) => void;
}

export const EndpointEditorConsoleFilter: FC<IEndpointEditorConsoleFiltersProps> = ({
	model,
	severityFilter,
	onFilterSelect
}) => {
	const { panel } = useLayoutContext();

	const [ parseErrorCount, setParseErrorCount ] = useState(() => sumErrorCounts(
		model.getDesignContext().getErrorTypeCounts()
	));

	const [ errorCount, setErrorCount ] = useState(() => getTotalErrorCounts(model.getDesignContext()));

	const consoleOpen = useMemo(() => !panel || panel?.isExpanded, [ panel ]);

	const getToggleFilter = useCallback(
		(item) => () => {
			if (isFunction(onFilterSelect)) {
				const deselect = severityFilter === item && consoleOpen;
				onFilterSelect(deselect ? null : item);
			}

			if (panel && !panel.isExpanded) {
				panel.expand();
			}
		},
		[ severityFilter, onFilterSelect, panel, consoleOpen ]
	);

	const handleClearParseErrors = useCallback(() => {
		model.clearAllParseErrors();
	}, [ model ]);

	const buttonClass = BEM("edt-view-editor-console-toolbar", "button");

	const isFilterActive = useDeepCompareCallback<(severity: DOC_ERROR_SEVERITY) => boolean>(
		(severity) => severityFilter?.has(severity),
		[ severityFilter ]
	);

	useEffect(() => {
		const handleErrorEvents = () => {
			setErrorCount(getTotalErrorCounts(
				model.getDesignContext()
			));

			setParseErrorCount(sumErrorCounts(
				model.getDesignContext().getErrorTypeCounts()
			));
		}

		onEvent(model.onRuntimeErrors, handleErrorEvents);
		onEvent(model.onParseErrors, handleErrorEvents);

		return () => {
			offEvent(model.onRuntimeErrors, handleErrorEvents);
			offEvent(model.onParseErrors, handleErrorEvents);
		};
	}, [ model ]);

	// @todo Translate
	// @todo Change colors to constants
	return (
		<div className={buttonClass.element("panel").toClassName()}>
			{parseErrorCount > 0 && (
				<ToolbarButton
					icon="mdi/notification-clear-all"
					label={`Clear Parse Errors`}
					onClick={handleClearParseErrors}
				/>	
			)}

			<ToolbarButton
				icon="mdi/alert-circle"
				className={buttonClass
					.mod({ severity: "error", highlighted: errorCount.error > 0 })
					.toClassName()}
				label={`${errorCount.error} errors`}
				active={isFilterActive(DOC_ERROR_SEVERITY.ERROR)}
				onClick={getToggleFilter(DOC_ERROR_SEVERITY.ERROR)}
			/>
			<ToolbarButton
				icon="mdi/alert"
				className={buttonClass
					.mod({ severity: "warning", highlighted: errorCount.warning > 0 })
					.toClassName()}
				label={`${errorCount.warning} warnings`}
				active={isFilterActive(DOC_ERROR_SEVERITY.WARNING)}
				onClick={getToggleFilter(DOC_ERROR_SEVERITY.WARNING)}
			/>
			<ToolbarButton
				icon="mdi/lightbulb"
				className={buttonClass
					.mod({ severity: "hint", highlighted: errorCount.hint > 0 })
					.toClassName()}
				label={`${errorCount.hint} hints`}
				active={isFilterActive(DOC_ERROR_SEVERITY.HINT)}
				onClick={getToggleFilter(DOC_ERROR_SEVERITY.HINT)}
			/>
		</div>
	);
};
